import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { registry, type RegPart } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { ActionRow } from "@/components/ActionRow";
import { ErrorNote } from "@/components/ErrorNote";
import { Field } from "@/components/Field";
import { Freshness, KV } from "@/components/KV";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { StatGrid } from "@/components/StatGrid";
import { Select, SearchInput } from "@/components/inputs";
import { ago, dateTime, POLL } from "@/lib/format";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { KindSelect, useTemplates } from "@/modules/asset_catalog/common";
import { CompanySelect, PART_CHIP, PartChip, RQ, SUBCOMPONENT_KINDS, useRegistryCompanies } from "@/modules/registry/parts_common";
import { useCompanyScope } from "@/auth/companyScope";
import { CAP, useSession } from "@/auth/session";

const LINK_CHIP: StatusMap = {
  active: { label: "ACTIVE", color: "var(--ok)" },
  inventory: { label: "INVENTORY", color: "var(--teal)" },
  provisioned: { label: "PROVISIONED", color: "var(--teal)" },
  suspended: { label: "SUSPENDED", color: "var(--warn)" },
  retired: { label: "RETIRED", color: "var(--t-mute)" },
};

/** Parts: serialised components — enrol (single / CSV), see where each one
 *  sits, install into an asset slot, remove, allocate spares. */
export function PartsInventory() {
  const qc = useQueryClient();
  const tpl = useTemplates().data;
  const { companies } = useRegistryCompanies();
  const [kind, setKind] = useState("");
  const [state, setState] = useState("");
  const { company } = useCompanyScope();   // the app bar's (D-134)
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<string | null>(null);

  const stats = useQuery({ queryKey: [...RQ, "parts", "stats"], queryFn: registry.partsStats, refetchInterval: POLL.LIST });
  const q = useQuery({
    queryKey: [...RQ, "parts", kind, state, company, search],
    queryFn: () => registry.parts({ kind: kind || undefined, state: state || undefined, company: company || undefined,
      search: search || undefined, limit: "500" }),
    refetchInterval: POLL.LIST,
  });
  const detail = useQuery({ queryKey: [...RQ, "part", sel], queryFn: () => registry.part(sel!), enabled: !!sel });
  const refresh = () => qc.invalidateQueries({ queryKey: [...RQ] });

  const dim = <span style={{ color: "var(--t-dim)" }}>—</span>;
  const cols = useMemo<DataCol<RegPart>[]>(() => [
    { key: "kind", priority: 2, label: "Kind", width: 130, render: (p) => p.kind_label },
    { key: "model", priority: 2, label: "Model", width: 170, render: (p) => <>{p.model} <span className="mono muted">v{p.model_version}</span></> },
    { key: "mfr", priority: 3, label: "Manufacturer", width: 130, render: (p) => p.manufacturer },
    { key: "serial", priority: 1, label: "Serial", width: 150, mono: true, render: (p) => p.serial_no },
    { key: "conn", priority: 2, label: "Connection ID", width: 150, mono: true, render: (p) => p.connection_id ?? dim },
    { key: "ble", priority: 3, label: "BLE MAC", width: 150, mono: true, render: (p) => p.ble_mac ?? dim },
    { key: "link", priority: 3, label: "Link", width: 100, render: (p) => p.conn_state ? <StatusChip st={p.conn_state} map={LINK_CHIP} /> : dim },
    { key: "fw", priority: 3, label: "FW", width: 56, mono: true, render: (p) => p.app_ver ?? dim },
    { key: "seen", priority: 2, label: "Last seen", width: 90, render: (p) => p.connection_id ? <Freshness ts={p.last_seen} /> : dim },
    { key: "state", priority: 2, label: "State", width: 100, render: (p) => <PartChip st={p.state} /> },
    // Owner and custodian side by side. They differ whenever something is away
    // — at a repairer, at an assembler, sold-but-not-yet-shipped — and that is
    // exactly when a person goes looking for it.
    { key: "owner", priority: 2, label: "Owner", width: 130, render: (p) => p.tenant ?? p.tenant_id },
    { key: "custodian", priority: 3, label: "Held by", width: 130,
      render: (p) => p.custodian_id === p.tenant_id
        ? <span className="muted">owner</span>
        : <span style={{ color: "var(--warn)" }}>{p.custodian ?? p.custodian_id}</span> },
    { key: "in", priority: 2, label: "Installed in", width: 190, mono: true, render: (p) => p.installed_in ? `${p.installed_in} · ${p.slot}` : dim },
    { key: "warranty", priority: 3, label: "Warranty", width: 100, mono: true, render: (p) => p.warranty_until ?? dim },
    { key: "enrolled", priority: 3, label: "Enrolled", width: 90, mono: true, render: (p) => ago(p.created_at), title: (p) => dateTime(p.created_at) },
  ], []);

  const st = stats.data;
  const d = detail.data;

  return (
    <div>
      <StatGrid stats={[
        { k: "Parts", v: st?.parts ?? "—" },
        // "in stock" is no longer Aphelion's — inventory is always SOMEBODY's.
        { k: "In inventory", v: st?.inventory ?? "—" },
        { k: "Deployed", v: st?.deployed ?? "—" },
        { k: "Installed", v: st?.installed ?? "—", s: "fitted to an asset" },
        { k: "Repair / scrapped", v: st ? `${st.repair} / ${st.scrapped}` : "—" },
      ]} />
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SearchInput placeholder="Search serial / IMEI / model…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
        <KindSelect kinds={tpl?.kinds ?? []} only={SUBCOMPONENT_KINDS} value={kind} onChange={setKind} placeholder="All kinds" />
        <Select value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">All states</option>
          {Object.entries(PART_CHIP).map(([k, v]) => <option key={k} value={k}>{v.label.toLowerCase()}</option>)}
        </Select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>{q.data ? `${q.data.total} parts` : ""}</span>
      </div>
      <DataTable rows={q.data?.parts ?? []} cols={cols} rowKey={(r) => r.part_id} pageSize={100}
        onRowClick={(r) => setSel(r.part_id)}
        empty={<div className="panel panel-pad cap">
          No parts yet - enrol the first one on the Enroll page (What = Part).
        </div>} />

      {sel && d && (
        <Drawer onClose={() => setSel(null)} width="min(620px,96vw)"
          header={<>
            <span style={{ fontWeight: 700 }}>{d.kind_label}</span>
            <span className="mono cap">{d.serial_no}</span>
            <PartChip st={d.state} />
          </>}>
          <div className="section-label">Identity</div>
          <KV k="Model" v={`${d.model} v${d.model_version}`} mono={false} />
          <KV k="Manufacturer" v={d.manufacturer} mono={false} />
          <KV k="Serial" v={d.serial_no} />
          {!d.connection_id && <KV k="Board ID" v={d.board_id ?? "same as serial"} />}
          <KV k="Connection ID" v={d.connection_id ?? (tpl?.kinds.find((k) => k.id === d.kind)?.conn ? "(missing!)" : "not a connected part")} />
          <BleMacRow part={d} onChanged={() => { void refresh(); }} />
          <KV k="Company" v={d.tenant ?? "In stock (Aphelion)"} mono={false} />
          <KV k="Installed in" v={d.installed_in ? `${d.installed_in} · ${d.slot}` : "—"} />
          {d.connection_id && <KV k="Link" v={`${d.conn_state ?? "—"} · FW ${d.app_ver ?? "—"} · seen ${d.last_seen ? ago(d.last_seen) : "never"}`} mono={false} />}
          <KV k="Manufactured" v={d.manufactured_at ?? "—"} />
          <KV k="Warranty until" v={d.warranty_until ?? "—"} />
          <KV k="Lot" v={d.lot ?? "—"} />
          <KV k="Enrolled" v={`${dateTime(d.created_at)} · ${d.created_by}`} />

          <PartActions part={d} companies={companies} onChanged={() => { void refresh(); }} />

          <div className="section-label tight">Installation history</div>
          {d.installations.length === 0 && <div className="cap">Never installed.</div>}
          {d.installations.map((i, n) => (
            <KV key={n} k={`${i.asset_id} · ${i.slot}`} accent={i.valid_to ? "var(--t-mute)" : "var(--ok)"}
              v={`${dateTime(i.valid_from)} → ${i.valid_to ? dateTime(i.valid_to) + (i.reason ? ` (${i.reason})` : "") : "now"}`} />
          ))}
          {d.connections.length > 0 && <>
            <div className="section-label tight">Connection history</div>
            {d.connections.map((c, n) => (
              <KV key={n} k={c.connection_id} accent={c.valid_to ? "var(--t-mute)" : "var(--ok)"}
                v={`${dateTime(c.valid_from)} → ${c.valid_to ? dateTime(c.valid_to) : "now"}`} />
            ))}
          </>}
          <div className="section-label tight">Journal</div>
          {d.events.map((e, n) => (
            <div key={n} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid var(--line)", display: "flex", gap: 10 }}>
              <span className="mono" style={{ color: "var(--t-mute)", whiteSpace: "nowrap" }}>{dateTime(e.ts)}</span>
              <span style={{ fontWeight: 600 }}>{e.event}</span>
              <span className="muted">{e.field ? `${e.field}: ${e.old ?? "—"} → ${e.new ?? "—"}` : ""}{e.actor ? ` · ${e.actor}` : ""}</span>
            </div>
          ))}
        </Drawer>
      )}
    </div>
  );
}

/** Install (in-stock) / Remove (installed) / Allocate (not installed). */
function PartActions({ part, companies, onChanged }: {
  part: RegPart; companies: { company_id: string; name: string }[]; onChanged: () => void;
}) {
  const fb = useMutationFeedback();
  const tpl = useTemplates().data;
  // Hoisted: the transfer picker below is inside a `{... && (...)}` branch, and
  // a hook called from there runs conditionally.
  const { scoped } = useRegistryCompanies();
  const [assetId, setAssetId] = useState("");
  const [slot, setSlot] = useState("");
  const [company, setCompany] = useState("");
  const [msg, setMsg] = useState("");
  const assets = useQuery({ queryKey: [...RQ, "assets", "pick"], queryFn: () => registry.assets({ limit: "500" }) });
  const asset = assets.data?.assets.find((a) => a.asset_id === assetId);
  const slots = (asset ? tpl?.slots[asset.asset_type] ?? [] : []).filter((s) => s.allow.includes(part.kind));

  const { can: canDo } = useSession();
  const install = useMutation({
    mutationFn: () => registry.installPart(part.part_id, { asset_id: assetId, slot }),
    onSuccess: (r) => { fb.ok(); setMsg(`installed in ${assetId} · ${slot}${r.warning ? ` - ${r.warning}` : ""}`); onChanged(); },
    onError: fb.fail,
  });
  const alloc = useMutation({
    mutationFn: () => registry.transferParts({ part_ids: [part.part_id], company_id: company }),
    onSuccess: (r) => { fb.ok(); setMsg(r.results[0].action + (r.results[0].reason ? ` - ${r.results[0].reason}` : "")); onChanged(); },
    onError: fb.fail,
  });
  const row: React.CSSProperties = { display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginTop: 10 };
  return (
    <div style={{ marginTop: 18 }}>
      <div className="section-label">Actions</div>
      {part.state === "inventory" && !part.installed_in && canDo(CAP.INSTALL_REMOVE) && (
        <div style={row}>
          <Field label="Install into asset">
            <Select value={assetId} onChange={(e) => { setAssetId(e.target.value); setSlot(""); }} style={{ minWidth: 220 }}>
              <option value=""> - choose asset - </option>
              {(assets.data?.assets ?? []).filter((a) => !!a.version_id && (a.tenant_id === part.tenant_id || part.tenant_id === null))
                .map((a) => <option key={a.asset_id} value={a.asset_id}>{a.asset_id} · {a.model}{a.tenant ? ` · ${a.tenant}` : ""}</option>)}
            </Select>
          </Field>
          <Field label="Slot">
            <Select value={slot} disabled={!assetId} onChange={(e) => setSlot(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">{assetId && !slots.length ? "no slot takes this kind" : "slot"}</option>
              {slots.map((s) => <option key={s.slot} value={s.slot}>{s.role}</option>)}
            </Select>
          </Field>
          <button className="btn teal" disabled={!assetId || !slot || install.isPending} onClick={() => install.mutate()}>Install</button>
        </div>
      )}
      {/* REMOVE-FROM-ASSET IS NOT HERE. Dattatray: *"Registry -> Inventory:
          from the side drawer remove this option, it's not required here. It's
          already there in Pair and Repair!"*

          It is, and Pair & Repair is the better home for the same reason the
          duplicate was worse than merely redundant: there the control sits
          BESIDE THE SLOT the part is fitted in, with the rest of the bill of
          materials on screen - so you can see what you are about to leave
          empty, and whether a sibling slot needs the same part. This drawer
          shows one part and no assembly, which is exactly the context the
          decision needs.

          Both rendered the same `SlotRow` mutation, so nothing is lost - 
          `registry.removePart` still has its one caller. */}
      {/* Transfer is a SALE - TRANSFER_OWNERSHIP is internal-only. */}
      {!part.installed_in && canDo(CAP.TRANSFER_OWNERSHIP) && (
        <div style={row}>
          <Field label="Transfer spare to">
            <CompanySelect value={company} onChange={setCompany} companies={companies} scoped={scoped} allLabel={null} style={{ minWidth: 200 }} />
          </Field>
          <button className="btn teal" disabled={!company || alloc.isPending} onClick={() => alloc.mutate()}>Allocate</button>
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 12 }}>
        {fb.errorNode}{!fb.err && msg && <span style={{ color: "var(--ok)" }}>{msg}</span>}
      </div>
    </div>
  );
}

/** Correct a part's BLE MAC in place (D-106).
 *
 *  Dattatray: *"today I have enrolled a few connected cluster meters with
 *  either dummy or test BLE IDs. I want a way to edit the BLE ID."* There was
 *  no way — `ble_mac` was set at enrol and the registry had no part-edit door.
 *
 *  ONLY this field is editable here, and the drawer says so by having exactly
 *  one pencil. Serial, board id and connection id are IDENTITY; the BLE MAC is
 *  a fact the device knows about itself (the ICCID's category, OCN-05), typed
 *  by hand only until the telemetry stream carries it. Giving the other fields
 *  an edit door because they sit beside this one is the mistake to avoid.
 *
 *  THE SAME COLON-GROUPING AS THE ENROL FORM, deliberately: an operator
 *  retyping a MAC they are comparing against a sticker should not have to
 *  think about separators, and two different input behaviours for one field in
 *  two places is how a transcription error gets blamed on the server.
 */
function BleMacRow({ part, onChanged }: { part: RegPart; onChanged: () => void }) {
  const { can } = useSession();
  const mayEdit = can(CAP.ENROL);
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(part.ble_mac ?? "");

  const save = useMutation({
    mutationFn: () => registry.setPartBleMac(part.part_id, { ble_mac: v.trim() || null }),
    onSuccess: () => { setEditing(false); onChanged(); },
  });

  // Typing: keep hex, regroup into pairs. Deleting: leave it alone, or the
  // colon the user just removed reappears and the caret jumps.
  const onType = (next: string) => {
    if (next.length < v.length) { setV(next); return; }
    const hex = next.replace(/[^0-9a-fA-F]/g, "").toUpperCase().slice(0, 12);
    setV((hex.match(/.{1,2}/g) ?? []).join(":"));
  };

  if (!editing) {
    return (
      <KV k="BLE MAC" v={
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="mono">{part.ble_mac ?? "no BLE on this model"}</span>
          {/* `sm` is the class for an action ON A ROW - not an inline padding
              override, which is what `tests/ui` refuses. The comment sits
              OUTSIDE the `&&`: inside it, it is a second expression and the
              JSX does not parse. */}
          {mayEdit && part.ble_mac !== undefined && (
            <button className="btn ghost sm"
              onClick={() => { setV(part.ble_mac ?? ""); setEditing(true); }}>
              Edit
            </button>
          )}
        </span>
      } mono={false} />
    );
  }

  return (
    <div style={{ padding: "6px 0" }}>
      <Field label="BLE MAC">
        <input className="inp mono" value={v} placeholder="AA:BB:CC:DD:EE:FF"
          onChange={(e) => onType(e.target.value)} />
      </Field>
      <ErrorNote error={save.error} />
      <ActionRow>
        <button className="btn ghost" onClick={() => setEditing(false)}>Cancel</button>
        <button className="btn teal" disabled={save.isPending}
          onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save BLE MAC"}
        </button>
      </ActionRow>
    </div>
  );
}
